/** Matches backend `SEASON_ORDER` in tariff.service.ts. */
export const SEASON_ORDER: Record<string, number> = {
    SUMMER: 0,
    WINTER: 1,
    ALL: 2,
}

/** Matches backend `TOU_ORDER` in tariff.service.ts. */
export const TOU_ORDER: Record<string, number> = {
    PEAK: 0,
    MID_PEAK: 1,
    PARTIAL_PEAK: 2,
    OFF_PEAK: 3,
    SUPER_OFF_PEAK: 4,
    ALL: 5,
}

export type SortDir = 'ASC' | 'DESC'

export type SortState<K extends string> = {
    key: K
    dir: SortDir
}

export type RateTableSortKey =
    | 'name'
    | 'rate'
    | 'type'
    | 'category'
    | 'season'
    | 'touPeriod'
    | 'rateGroup'
    | 'rateEffectiveDate'
    | 'rateEndDate'
    | 'vintageYear'
    | 'modifiedDescription'
    | 'cuid'

export type ValidationTableSortKey =
    | 'schedule'
    | 'season'
    | 'touPeriod'
    | 'totalRate'
    | 'generationRate'
    | 'deliveryRate'
    | 'nbcRate'
    | 'deliveryPlusNbc'
    | 'deliveryPlusNbcWithoutWildfire'
    | 'vintageYear'
    | 'generationDelta'
    | 'deliveryDelta'
    | 'nbcDelta'

type SeasonTouRow = {
    season?: string | null
    touPeriod?: string | null
    name?: string | null
}

export function nextSortState<K extends string>(current: SortState<K> | null, key: K): SortState<K> | null {
    if (current == null || current.key !== key) return { key, dir: 'ASC' }
    if (current.dir === 'ASC') return { key, dir: 'DESC' }
    return null
}

export function enumOrder(order: Record<string, number>, value: string | null | undefined): number {
    if (value == null || value === '') return 99
    return order[value] ?? 99
}

export function compareNullable(a: unknown, b: unknown): number {
    if (a == null && b == null) return 0
    if (a == null) return 1
    if (b == null) return -1
    if (typeof a === 'number' && typeof b === 'number') {
        if (Number.isNaN(a) && Number.isNaN(b)) return 0
        if (Number.isNaN(a)) return 1
        if (Number.isNaN(b)) return -1
        return a - b
    }
    return String(a).localeCompare(String(b), undefined, { numeric: true })
}

export function compareSeasonThenTou(a: SeasonTouRow, b: SeasonTouRow): number {
    const seasonDiff = enumOrder(SEASON_ORDER, a.season) - enumOrder(SEASON_ORDER, b.season)
    if (seasonDiff !== 0) return seasonDiff
    const touDiff = enumOrder(TOU_ORDER, a.touPeriod) - enumOrder(TOU_ORDER, b.touPeriod)
    if (touDiff !== 0) return touDiff
    return (a.name ?? '').localeCompare(b.name ?? '')
}

function applyDir(diff: number, dir: SortDir): number {
    return dir === 'ASC' ? diff : -diff
}

function vintageValue(value: string | null | undefined): number | string | null {
    if (value == null || value === '') return null
    const asNumber = Number(value)
    return Number.isFinite(asNumber) ? asNumber : value
}

export function compareRateRows<T extends SeasonTouRow & Record<string, unknown>>(
    a: T,
    b: T,
    sort: SortState<string> | null,
    valueOf: (row: T, key: string) => unknown
): number {
    if (sort == null) return compareSeasonThenTou(a, b)
    const primary = applyDir(compareNullable(valueOf(a, sort.key), valueOf(b, sort.key)), sort.dir)
    if (primary !== 0) return primary
    if (sort.key === 'season') {
        const tou = applyDir(
            enumOrder(TOU_ORDER, a.touPeriod) - enumOrder(TOU_ORDER, b.touPeriod),
            sort.dir
        )
        if (tou !== 0) return tou
    } else if (sort.key === 'touPeriod') {
        const season = applyDir(
            enumOrder(SEASON_ORDER, a.season) - enumOrder(SEASON_ORDER, b.season),
            sort.dir
        )
        if (season !== 0) return season
    } else {
        const seasonTou = compareSeasonThenTou(a, b)
        if (seasonTou !== 0) return seasonTou
    }
    return (a.name ?? '').localeCompare(b.name ?? '')
}

export function rateTableValue(
    row: {
        name: string
        rate: number
        type: string
        category: string
        season: string | null
        touPeriod: string | null
        rateGroup: string
        rateEffectiveDate: string
        rateEndDate: string | null
        vintageYear: string | null
        modifiedDescription: string | null
        cuid: string
    },
    key: string
): unknown {
    switch (key) {
        case 'name':
            return row.name
        case 'rate':
            return row.rate
        case 'type':
            return row.type
        case 'category':
            return row.category
        case 'season':
            return enumOrder(SEASON_ORDER, row.season)
        case 'touPeriod':
            return enumOrder(TOU_ORDER, row.touPeriod)
        case 'rateGroup':
            return row.rateGroup
        case 'rateEffectiveDate':
            return row.rateEffectiveDate
        case 'rateEndDate':
            return row.rateEndDate
        case 'vintageYear':
            return vintageValue(row.vintageYear)
        case 'modifiedDescription':
            return row.modifiedDescription
        case 'cuid':
            return row.cuid
        default:
            return null
    }
}

export function validationTableValue(
    row: {
        schedule: string
        season: string | null
        touPeriod: string | null
        vintageYear: string | null
        totalRate: number
        generationRate: number
        deliveryRate: number
        nbcRate: number
        deliveryPlusNbc: number
        deliveryPlusNbcWithoutWildfire: number
        generationDelta: number | null
        deliveryDelta: number | null
        nbcDelta: number | null
    },
    key: string
): unknown {
    switch (key) {
        case 'schedule':
            return row.schedule
        case 'season':
            return enumOrder(SEASON_ORDER, row.season)
        case 'touPeriod':
            return enumOrder(TOU_ORDER, row.touPeriod)
        case 'vintageYear':
            return vintageValue(row.vintageYear)
        case 'totalRate':
            return row.totalRate
        case 'generationRate':
            return row.generationRate
        case 'deliveryRate':
            return row.deliveryRate
        case 'nbcRate':
            return row.nbcRate
        case 'deliveryPlusNbc':
            return row.deliveryPlusNbc
        case 'deliveryPlusNbcWithoutWildfire':
            return row.deliveryPlusNbcWithoutWildfire
        case 'generationDelta':
            return row.generationDelta
        case 'deliveryDelta':
            return row.deliveryDelta
        case 'nbcDelta':
            return row.nbcDelta
        default:
            return null
    }
}
